import {
  loadGoogleMaps as load,
  tilesAreConfiguredWith,
  type GoogleMapsGlobal,
} from "@tdelivr/google-maps";

/**
 * This console's Google Maps key, and nothing else.
 *
 * Everything that was in this file is now `@tdelivr/google-maps`, because the rider and driver
 * apps draw maps too and three copies of a loader is three places for the next loading bug to
 * hide — and there have been two already, both of them invisible to every test and visible only
 * on the deployed page. What cannot move is this: the name of the environment variable. Vite
 * bakes `import.meta.env.VITE_*` in at build time, per application, so the read has to happen in
 * the application. The package takes the key as an argument for exactly that reason.
 *
 * ## The key is optional, and that is load-bearing rather than defensive
 *
 * With no `VITE_GOOGLE_MAPS_API_KEY` the map draws what it drew before: the decorative surface,
 * and `projectionFor`'s equirectangular fit. Three things depend on that staying true:
 *
 *   - **The design-fidelity check.** `check-live-map.mts` screenshots this build against
 *     `05 Dispatch Live Map.dc.html`, and the design draws an empty box too — its own note says
 *     *"Map provider open · vendor-neutral surface"*. Tiles in the keyless render would make that
 *     comparison fail for ever against a design that has no tiles, which would mean weakening a
 *     check rather than passing it.
 *   - **Every test.** They run with no network and no key.
 *   - **Anybody without a key.** A console that cannot draw its main screen because a billing
 *     account is missing is worse than one that draws a schematic.
 *
 * So the tiles are an enhancement over a surface that already works, and the keyless path is not
 * a fallback — it is the path the design describes.
 */
export const MAPS_API_KEY: string | undefined =
  typeof import.meta.env.VITE_GOOGLE_MAPS_API_KEY === "string" &&
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY.length > 0
    ? import.meta.env.VITE_GOOGLE_MAPS_API_KEY
    : undefined;

/** Whether this build should draw tiles at all. One question, asked in one place. */
export function tilesAreConfigured(): boolean {
  return tilesAreConfiguredWith(MAPS_API_KEY);
}

/** The API, loaded once for this console, under this console's key. */
export function loadGoogleMaps(): Promise<GoogleMapsGlobal> {
  return load(MAPS_API_KEY);
}

export {
  boundsOf,
  MAP_OPTIONS,
  onMapsAuthFailure,
  projectionFromMap,
  READY_CALLBACK,
  tilesAreConfiguredWith,
  type Coordinate,
  type GoogleMap,
  type GoogleMapsGlobal,
  type Placement,
  type Projection,
} from "@tdelivr/google-maps";
